import {
    AfterViewInit,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
    finalize,
    firstValueFrom,
    map,
    Observable,
    startWith,
    Subject,
    takeUntil,
    takeWhile,
    tap,
    timer,
} from 'rxjs';
import { EmpresaService } from '@shared/service/app/empresa.service';
import { IProveedor } from 'app/modules/administracion/proveedores/models/proveedor';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { MY_DATE_FORMATS } from '@shared/constants/date-format';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { IAlertConfig } from '@shared/models/alert';
import { EnviarEmailModalService } from './enviar-email.service';
import { IImagenAdjunto } from '@shared/models/imagen-adjunto';
import { QuillEditorComponent } from 'ngx-quill';
import { ICreateEmail, SelectedEmail } from './enviar-email';
import { IPayloadEmail, TipoDestinatario, Tipos } from './email.payload';
import { AppAlertService } from '@shared/service/app/alert.service';
import { DisplayTextService } from '@shared/service/app/display-text.service';
import { FilterService } from '@shared/service/app/filter.service';
import { ICliente } from 'app/modules/administracion/clientes/models/cliente';
import { fuseAnimations } from '@fuse/animations';
import { IEmpleado } from 'app/modules/rrhh/empleados/model/empleado';

@Component({
    selector: 'app-enviar-email-component',
    templateUrl: './enviar-email.component.html',
    styleUrls: ['./enviar-email.component.scss'],
    providers: [{ provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS }],
    animations: fuseAnimations
})
export class EnviarEmailModalComponent implements OnInit, AfterViewInit {
    // Necesarios para la aplicacion
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    form: FormGroup;
    color_primario: string;
    color_secundario: string;
    default_color: string = 'bg-primary';
    showAlert = false;
    _alertConfig: IAlertConfig;
    mostrarSpinner = true;
    editorIsReady = false;
    mostrarAdvertenciaDestinatarioSinEmail = false;

    // Particulares del componente
    model;
    clientes: ICliente[];
    filteredClientes: Observable<ICliente[]>;
    proveedores: IProveedor[];
    filteredProveedores: Observable<IProveedor[]>;
    empleados: IEmpleado[];
    filteredEmpleados: Observable<IEmpleado[]>;
    selectedEmails: SelectedEmail[] = [];
    remitentesSugeridos: string[] = [];
    filteredRemitentes: Observable<string[]>;
    titleImagen: boolean;
    protected fileAdjuntoImagen: any;
    protected validExtension = ['.pdf'];
    selectedFiles: File[] = [];
    imagen: string;
    adjunto: IImagenAdjunto | null = null;
    adjuntos = [];
    selectedOption: string = '';
    isHovering: boolean;
    readonly separatorKeysCodes = [ENTER, COMMA];
    readonly addOnBlur = true;

    // Configuración de la barra de herramientas
    editorModules = {
        toolbar: [
            ['bold', 'italic', 'underline', 'strike'], // Estilos de texto
            [{ header: [1, 2, 3, false] }], // Tamaños de encabezado
            [{ list: 'ordered' }, { list: 'bullet' }], // Listas
            ['link', 'image'], // Enlaces, imágenes y videos
            ['clean'], // Eliminar formato
        ],
    };

    @Output() public enviadoExitosamente: EventEmitter<void> =
        new EventEmitter<void>();

    @ViewChild(QuillEditorComponent) quillEditor: QuillEditorComponent;

    @Input() public permiteAdjunto: boolean = false;
    @Input() public _endpointService: string = '';
    @Input() public title: string = 'Enviar Email';
    @Input() public tipo: Tipos = 'COMPRAS';
    @Input() public destinatarioTipo: TipoDestinatario = 'PROVEEDOR';

    //  Configuracion para Quill
    editorContent =
        'Estimado/a, Adjuntamos solicitud generada a través de nuestro sistema. Por favor, revise el documento adjunto y, en caso de requerir más información, no dude en contactarnos. Quedamos atentos a su respuesta. Saludos cordiales,'; // Contenido del editor

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: IPayloadEmail,
        private _enviarEmailModalService: EnviarEmailModalService,
        private _empresaService: EmpresaService,
        private _filterService: FilterService,
        private _displayTextService: DisplayTextService,
        private alert: AppAlertService,
        public dialogRef: MatDialogRef<EnviarEmailModalComponent>
    ) {
        // Elementos parametrizables
        this._endpointService = data._endpointService;
        this.title = data.title;
        this.permiteAdjunto = data.permiteAdjunto;
        this.model = data.model;
        this.editorContent = data.defaultMessage ?? this.editorContent;
        this.tipo = data.tipo ?? 'COMPRAS';
        this.destinatarioTipo = data.destinatarioTipo ?? 'PROVEEDOR';

        // Subscribe to empresa data
        this._empresaService.empresa$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((empresa) => {
                this.color_primario = empresa.color_primario;
                this.color_secundario = empresa.color_secundario;
            });
    }

    getTextGenerico = (option: any) => this._displayTextService.getTextGenerico(option);

    get esDestinatarioEmpleado(): boolean {
        return this.tipo === 'COMPRAS' && this.destinatarioTipo === 'EMPLEADO';
    }

    get etiquetaDestinatario(): string {
        if (this.tipo === 'VENTAS') {
            return 'Cliente';
        }

        return this.esDestinatarioEmpleado ? 'Empleado' : 'Proveedor';
    }

    ngAfterViewInit(): void {
        // La instancia debería estar disponible en ngAfterViewInit.
        if (this.quillEditor) {
            this.editorIsReady = true;
            if (this.editorIsReady) {
                this.mostrarSpinner = false;
            }
        }
    }

    onHover(hovering: boolean, $event: MouseEvent) {
        $event.preventDefault();
        this.isHovering = hovering;

        const target = $event.target as HTMLElement;
        const span = target.querySelector('span');

        if (span) {
            span.style.color = hovering ? 'white' : '';
        }

        if (hovering) {
            target.style.setProperty(
                'background-color',
                this.color_primario,
                'important'
            );
        } else {
            target.style.removeProperty('background-color');
            target.style.removeProperty('color');
        }
    }

    // Alert
    subscribeAlert() {
        this.alert.emitConfig.subscribe({
            next: (config: IAlertConfig) => {
                this.showAlert = true;
                this._alertConfig = {
                    ...config,
                    appearance: 'border',
                };
                let time = 2;
                timer(100, 1500)
                    .pipe(
                        finalize(() => {
                            this.showAlert = false;
                            this._alertConfig = null;
                        }),
                        takeWhile(() => time > 0),
                        takeUntil(this._unsubscribeAll),
                        tap(() => (time -= 1))
                    )
                    .subscribe();
            },
        });
    }

    ngOnInit(): void {
        this.newForm();
        this.initRemitentes();
        if (this.tipo === 'VENTAS') {
            this.getClientes(this.model.cliente ?? null, true);
        } else if (this.esDestinatarioEmpleado) {
            this.getEmpleados(this.model?.empleado ?? this.model?.idempleado ?? null, true);
        } else {
            this.getProveedores(this.model.proveedor ?? null, true);
        }

        this.subscribeAlert();
    }

    private initRemitentes(): void {
        const storedUser = localStorage.getItem('user') || sessionStorage.getItem('user');
        let userName = '';
        if (storedUser) {
            try {
                const parsed = JSON.parse(storedUser);
                userName = `${parsed.nombre || ''} ${parsed.apellido || ''}`.trim() || parsed.name || parsed.nick || '';
            } catch (e) {
                userName = '';
            }
        }

        const opciones: string[] = [];
        const defaultRemitente = this.data?.defaultNombreRemitente || userName;
        if (defaultRemitente) {
            opciones.push(defaultRemitente);
            this.form.get('nombre_remitente')?.setValue(defaultRemitente);
        }

        if (userName && !opciones.includes(userName)) {
            opciones.push(userName);
        }

        const datosEmpresa = this._empresaService._empresaData;
        if (datosEmpresa?.razon_social && !opciones.includes(datosEmpresa.razon_social)) {
            opciones.push(datosEmpresa.razon_social);
        }

        const tipoEmpresaMail = this.tipo === 'VENTAS' ? 1 : 2;
        this._enviarEmailModalService.getEmpresaMails(tipoEmpresaMail).pipe(takeUntil(this._unsubscribeAll)).subscribe({
            next: (mails) => {
                if (Array.isArray(mails)) {
                    mails.forEach((m) => {
                        const remitente = m.nombre_remitente?.trim();
                        if (remitente && !opciones.includes(remitente)) {
                            opciones.push(remitente);
                        }
                    });
                }
                this.remitentesSugeridos = [...opciones];
            },
            error: () => {
                this.remitentesSugeridos = [...opciones];
            }
        });

        this.remitentesSugeridos = [...opciones];
        this.filteredRemitentes = this.form.get('nombre_remitente')!.valueChanges.pipe(
            startWith(this.form.get('nombre_remitente')?.value || ''),
            map((val) => {
                const search = (typeof val === 'string' ? val : '').toLowerCase().trim();
                if (!search) {
                    return this.remitentesSugeridos;
                }
                return this.remitentesSugeridos.filter((op) => op.toLowerCase().includes(search));
            })
        );
    }

    newForm() {
        this.form = new FormGroup({
            proveedor: new FormControl(null, [this.validarDestinatarioSeleccionado]),
            nombre_remitente: new FormControl(this.data?.defaultNombreRemitente || ''),
            nombre_adjunto: new FormControl(this.data?.defaultNombreAdjunto || ''),
            asunto: new FormControl(null, Validators.required),
        });
    }

    selectProveedor(
        $event: MatAutocompleteSelectedEvent,
        input: HTMLInputElement
    ) {
        const prov: IProveedor = $event.option.value;
        const cliente: ICliente = $event.option.value;
        const empleado: IEmpleado = $event.option.value;
        const primaryMail = prov?.Mail ?? cliente?.correoElectronico ?? empleado?.mail;
        const altMail = prov?.mail_alternativo ?? cliente?.correoElectronicoAlternativo;
        const providerCode = prov?.Codigo ?? cliente?.codigo ?? empleado?.idempleado ?? null;
        const emailsAgregados =
            this.agregarEmailSeleccionado(primaryMail, providerCode, false) +
            this.agregarEmailSeleccionado(altMail, providerCode, true);

        this.mostrarAdvertenciaDestinatarioSinEmail = emailsAgregados === 0;
        this.actualizarValidacionDestinatario();

        this.form.get('proveedor').setValue('');
        input.value = '';
    }

    addEmailFromInput(event: MatChipInputEvent) {
        const input = event.input;
        const rawValue = (event.value || '').trim();

        if (!rawValue) {
            if (input) {
                input.value = '';
            }
            this.form.get('proveedor').setValue('');
            return;
        }

        const emails = rawValue
            .split(/[,\s;]+/)
            .map((value) => value.trim())
            .filter((value) => value.length > 0);

        for (const email of emails) {
            if (!this.isValidEmail(email)) {
                continue;
            }

            const normalized = email.toLowerCase();
            if (this.selectedEmails.some((e) => e.mail.toLowerCase() === normalized)) {
                continue;
            }

            this.selectedEmails.push({
                mail: email,
                providerCode: null,
                isAlternate: false
            });
        }

        if (input) {
            input.value = '';
        }
        this.form.get('proveedor').setValue('');
        this.actualizarValidacionDestinatario();
    }


    removeEmail(emailEntry: SelectedEmail) {
        const idx = this.selectedEmails.indexOf(emailEntry);
        if (idx >= 0) {
            this.selectedEmails.splice(idx, 1);
        }
        this.actualizarValidacionDestinatario();
    }

    onSubmit() {
        this.mostrarSpinner = true;
        const proveedorControl = this.form.get('proveedor');
        const asuntoControl = this.form.get('asunto');
        const asunto = `${asuntoControl?.value ?? ''}`.trim();

        proveedorControl?.markAsTouched();
        asuntoControl?.markAsTouched();

        if (this.selectedEmails.length === 0) {
            this.mostrarSpinner = false;
            this.alert.warning(`Debe ingresar al menos un correo para ${this.etiquetaDestinatario.toLowerCase()}.`);
            return;
        }

        if (!asunto) {
            this.mostrarSpinner = false;
            this.alert.warning('Debe completar el asunto antes de enviar.');
            return;
        }

        asuntoControl?.setValue(asunto, { emitEvent: false });

        if (this.selectedEmails.length > 0) {
            const proveedores_emails = this.selectedEmails.map(e => e.mail);
            const nombreRemitente = `${this.form.get('nombre_remitente')?.value ?? ''}`.trim();
            const nombreAdjunto = `${this.form.get('nombre_adjunto')?.value ?? ''}`.trim();

            let payload: ICreateEmail = {
                destinatarios: proveedores_emails,
                asunto,
                contenido: this.editorContent,
                nombre_remitente: nombreRemitente || null,
                nombre_adjunto: nombreAdjunto || null,
            };

            if (this.model.idpedido) payload['idpedido'] = this.model.idpedido;

            if (this.model.codigo && this.tipo === 'COMPRAS') payload['idordencompra'] = this.model.codigo;

            if (this.model.codigo && this.tipo === 'VENTAS') payload['idfactura'] = this.model.codigo;

            if (this.model.idpago) payload['idpago'] = this.model.idpago;

            this._enviarEmailModalService
                .enviarEmail(payload, this._endpointService)
                .subscribe({
                    next: (data) => {
                        if (data.success === true) {
                            this.enviadoExitosamente.emit(data);
                            this.onCloseMenu(data);
                        } else {
                            this.alert.error(data?.mensaje || 'Error al enviar email');
                        }
                        this.mostrarSpinner = false;
                    },
                    error: (error) => {
                        // Manejar el error
                        console.error('Error en el servicio:', error);
                        this.alert.error(error?.error?.mensaje || "Error al enviar Email");
                        this.mostrarSpinner = false;
                    }
                });
        }
    }

    onCloseMenu(data) {
        this.dialogRef.close(data);
    }

    // ################# Metodos de comunicacion con el service #################

    async getProveedores(id?: number, addAll = false) {
        const Proveedores = await firstValueFrom(
            this._enviarEmailModalService.getProveedores(id)
        );
        const proveedores_aux = this.model.idpedido
            ? this.data.model.proveedores.split(',').map((num) => parseInt(num))
            : [this.model.proveedor];

        new Promise(async (resolve) => {
            this.proveedores = Proveedores.list.filter((p) =>
                proveedores_aux.includes(p.Codigo)
            );
            this.filteredProveedores = this.form
                .get('proveedor')
                .valueChanges.pipe(
                    startWith(''),
                    map((value) => this._filterProveedores(value))
                );

            if (addAll) {
                let emailsAgregados = 0;
                this.proveedores.forEach((e) => {
                    emailsAgregados += this.agregarEmailSeleccionado(e.Mail, e.Codigo, false);
                    emailsAgregados += this.agregarEmailSeleccionado(e.mail_alternativo, e.Codigo, true);
                });
                this.mostrarAdvertenciaDestinatarioSinEmail = emailsAgregados === 0;
                this.actualizarValidacionDestinatario();
            }
            this.mostrarSpinner = false;
            resolve(true);
        });
    }

    async getClientes(option?, addAll = false) {
        const Clientes = await firstValueFrom(
            this._enviarEmailModalService.getClientes(option)
        );
        const clientes_aux = [this.model.cliente];

        new Promise(async (resolve) => {
            this.clientes = Clientes.list.filter((p) =>
                clientes_aux.includes(p.codigo)
            );

            this.filteredClientes = this.form
                .get('proveedor')
                .valueChanges.pipe(
                    startWith(''),
                    map((value) => this._filterService.filterSelect(value || '', this.clientes, 'razonSocial'))
                );

            if (addAll) {
                let emailsAgregados = 0;
                this.clientes.forEach((e) => {
                    emailsAgregados += this.agregarEmailSeleccionado(e.correoElectronico, e.codigo, false);
                    emailsAgregados += this.agregarEmailSeleccionado(
                        e.correoElectronicoAlternativo,
                        Number(e.cliente_asociado),
                        true
                    );
                });
                this.mostrarAdvertenciaDestinatarioSinEmail = emailsAgregados === 0;
                this.actualizarValidacionDestinatario();
            }
            this.mostrarSpinner = false;
            resolve(true);
        });
    }

    async getEmpleados(id?: number, addAll = false) {
        const Empleados = await firstValueFrom(
            this._enviarEmailModalService.getEmpleados()
        );
        const empleados_aux = typeof id === 'number' ? [id] : [];

        new Promise(async (resolve) => {
            this.empleados = Empleados.list.filter((e) =>
                empleados_aux.length ? empleados_aux.includes(e.idempleado) : true
            );

            this.filteredEmpleados = this.form
                .get('proveedor')
                .valueChanges.pipe(
                    startWith(''),
                    map((value) => this._filterEmpleados(value))
                );

            if (addAll && empleados_aux.length > 0) {
                let emailsAgregados = 0;
                this.empleados.forEach((e) => {
                    emailsAgregados += this.agregarEmailSeleccionado(e.mail, e.idempleado, false);
                });
                this.mostrarAdvertenciaDestinatarioSinEmail = emailsAgregados === 0;
                this.actualizarValidacionDestinatario();
            }
            this.mostrarSpinner = false;
            resolve(true);
        });
    }

    private agregarEmailSeleccionado(
        mail: string | null | undefined,
        providerCode: number | null,
        isAlternate: boolean
    ): number {
        const normalizedMail = (mail ?? '').trim();
        if (!normalizedMail) {
            return 0;
        }

        const alreadySelected = this.selectedEmails.some(
            (email) => email.mail?.toLowerCase() === normalizedMail.toLowerCase()
        );

        if (alreadySelected) {
            return 0;
        }

        this.selectedEmails.push({
            mail: normalizedMail,
            providerCode,
            isAlternate
        });

        return 1;
    }

    private validarDestinatarioSeleccionado = (_control: AbstractControl): ValidationErrors | null => {
        return this.selectedEmails.length > 0 ? null : { destinatarioRequerido: true };
    };

    private actualizarValidacionDestinatario(): void {
        this.form?.get('proveedor')?.updateValueAndValidity({ emitEvent: false });
    }

    // ################# Metodo de filtrado para inputs autocomplete #################
    private _filterProveedores(proveedor: IProveedor | string): IProveedor[] {
        const filterValue = (typeof proveedor === 'string' ? proveedor : this.getTextProveedor(proveedor)).toLowerCase();
        return this.proveedores.filter((item) => {
            const texto = `${item?.RazonSocial ?? ''} ${item?.Mail ?? ''} ${item?.mail_alternativo ?? ''}`.toLowerCase();
            return texto.includes(filterValue);
        });
    }

    private _filterEmpleados(empleado: IEmpleado | string): IEmpleado[] {
        const filterValue = (typeof empleado === 'string' ? empleado : this.getTextProveedor(empleado)).toLowerCase();
        return this.empleados.filter((item) => {
            const texto = `${item?.nombre ?? ''} ${item?.apellido ?? ''} ${item?.mail ?? ''}`.toLowerCase();
            return texto.includes(filterValue);
        });
    }

    // ################# Metodos para obtener textos de input #################

    getTextProveedor(option) {
        if (!option) {
            return '';
        }

        if (option.Mail) {
            return option.Mail;
        }

        if (option.correoElectronico) {
            return option.correoElectronico;
        }

        if (option.mail) {
            return option.mail;
        }

        const texto = this.getTextGenerico(option);
        if (texto) {
            return texto;
        }

        return '';
    }

    private isValidEmail(email: string): boolean {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    // ################# Metodos extra #################

    /**
     * Funcion para contar las palabras en las observaciones
     */
    getPalabrasLength(): number {
        const observaciones = this.form.get('observaciones').value;
        return observaciones ? observaciones.length : 0;
    }

    /**
     *
     * @param $event Archivo
     * @param tipo_imagen 0: imagen principal, 1: imagen secundaria
     */
    setImagen($event: Event, tipo_imagen: number) {
        const imagen_data = ($event.target as HTMLInputElement).files[0];

        let size = Math.round(imagen_data.size / 1024); // Convierto el tamano de la imagen a kilobytes
        let nombre_archivo = imagen_data.name; // Encapsulo el nombre del archivo
        let extension_archivo = nombre_archivo.split('.').pop(); // Obtengo la extension del archivo

        this.getBase64(imagen_data).then((imagen_en_base_64) => {
            // Valido el formato del archivo
            if (!this.validExtension.includes('.' + extension_archivo)) {
                return this.alert.info('Formato de archivo no soportado');
            }

            // Encapsulo los valores que me interesan de la imagen
            let data = {
                base64: imagen_en_base_64.toString().split('base64,')[1],
                name: nombre_archivo,
                size: size,
                extension: extension_archivo,
            };

            // Si es de tipo_imagen "principal"...
            if (tipo_imagen === 0) {
                // Guardo la informacion en una variable global
                this.adjuntos = [data, ...this.adjuntos];
            }
        });
    }

    async getBase64(file: any): Promise<any> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });
    }

    getFileNameFromUrl(url: string): string {
        // Divide la URL en partes utilizando el carácter "/"
        const parts = url.split('/');
        // El último elemento del array es el nombre del archivo
        const fileName = parts[parts.length - 1];
        return fileName;
    }

    getFileSize(size: number): string {
        const bytes = size;
        const kilobytes = bytes / 1024;
        if (kilobytes < 1024) {
            return kilobytes.toFixed(2) + ' KB';
        } else {
            const megabytes = kilobytes / 1024;
            return megabytes.toFixed(2) + ' MB';
        }
    }

    getFileTitle(file: File): string {
        return file.name + ' (' + this.getFileSize(file.size) + ')';
    }

    /**
     * Metodo parametrizado para eliminar los archivos multimedia segun el momento de persistencia
     * @param tipo Tipo de imagen que se quiere eliminar
     * @param file Archivo a eliminar
     */
    removeFile(tipo: string, file: any) {
        switch (tipo) {
            // Eliminar imagenes secundarias dadas de alta en la operacion actual
            case 'adjuntos':
                {
                    this.adjuntos = this.adjuntos.filter(
                        (adjunto) =>
                            adjunto.name.toLowerCase() !=
                            file.name.toLowerCase()
                    );
                }
                break;

            default: {
                console.error('Ocurrió un error al quitar el adjunto.');
            }
        }
    }
}
